import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { takePostLoginRedirect } from '../../utils/postLoginRedirect';
import { useAuthStore } from '../../store/authStore';
import { UserPlus, AlertCircle } from 'lucide-react';

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [validationError, setValidationError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    clearError();
    setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return;
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });
      navigate(takePostLoginRedirect(), { replace: true });
    } catch (error) {
      // Error is handled by the store
    }
  };

  return (
    <div className="min-h-screen bg-flow-purple flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-flow-purple rounded-full flex items-center justify-center mb-4">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-4xl font-bold text-flow-purple mb-2">
              Create Account
            </h2>
            <p className="font-sans text-text-gray">
              Join us to manage your tasks efficiently
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Error Messages */}
            {(error || validationError) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-sans">{error || validationError}</p>
              </div>
            )}

            {/* Full Name - NO ICON */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flow-purple focus:border-transparent transition-all bg-white font-sans"
                placeholder="John Doe"
              />
            </div>

            {/* Email - NO ICON */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flow-purple focus:border-transparent transition-all bg-white font-sans"
                placeholder="you@example.com"
              />
            </div>

            {/* Password - NO ICON */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flow-purple focus:border-transparent transition-all bg-white font-sans"
                placeholder="••••••••"
              />
              <p className="text-xs text-gray-500 mt-1 font-sans">Must be at least 6 characters</p>
            </div>

            {/* Confirm Password - NO ICON */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 font-sans">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flow-purple focus:border-transparent transition-all bg-white font-sans"
                placeholder="••••••••"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-flow-purple text-white hover:bg-primary-600 py-3 rounded-lg font-sans font-medium transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  <span>Create Account</span>
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-text-gray font-sans">
              Already have an account?{' '}
              <Link to="/login" className="text-flow-purple hover:text-primary-600 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};